/**
 * Breadcrumb navigation (Story 30.6) — visible `Home > Section > Page` trail
 * plus BreadcrumbList JSON-LD (30.4), from a single `items` list. The last
 * item is the current page (not a link, aria-current).
 */
import { Fragment } from "react";
import { JsonLd } from "../../seo/JsonLd";
import { breadcrumbListSchema } from "../../seo/schemas";
import { SITE_ORIGIN } from "../../seo/seoConfig";

export interface Crumb {
  name: string;
  /** Path ("/blog") or absolute URL. */
  url: string;
}

function absolute(url: string): string {
  return url.startsWith("http") ? url : `${SITE_ORIGIN}${url}`;
}

export function Breadcrumb({ items }: { items: Crumb[] }) {
  if (items.length === 0) return null;

  return (
    <>
      <JsonLd data={breadcrumbListSchema(items.map((c) => ({ name: c.name, url: absolute(c.url) })))} />
      <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
        <ol className="flex flex-wrap items-center gap-1.5">
          {items.map((c, i) => {
            const isLast = i === items.length - 1;
            return (
              <Fragment key={`${c.url}-${i}`}>
                <li>
                  {isLast ? (
                    <span aria-current="page" className="text-foreground">
                      {c.name}
                    </span>
                  ) : (
                    <a href={c.url} className="hover:text-foreground">
                      {c.name}
                    </a>
                  )}
                </li>
                {!isLast && <li aria-hidden>/</li>}
              </Fragment>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
